/**
 * Generator mutation hooks for Cloud Function CRUD operations.
 *
 * Follows the same pattern as useScheduleBlockMutations.ts:
 * - Cloud Function calls via httpsCallable
 * - Optimistic cache updates for instant feedback
 * - Rollback on error, revalidation on settle
 *
 * @module hooks/equipment/useGeneratorMutations
 *
 * @example
 * const { mutate: create } = useCreateGenerator();
 * create({ clientName: 'Acme Corp', equipmentType: 'Liquid-Cooled Standby Gen' });
 *
 * const { mutate: update } = useUpdateGenerator();
 * update({ generatorId: 'abc123', updates: { status: 'Inactive' } });
 *
 * const { mutate: remove } = useDeleteGenerator();
 * remove('abc123');
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { Timestamp } from 'firebase/firestore';
import { functions } from '../../config/firebase';
import { SET_GENERATOR_WORKFLOW_STATUS_CALLABLE_TIMEOUT_MS, UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS } from '../../config/generatorCallables';
import { dataHubEquipmentKeys } from '../useDataHubEquipment';
import { generatorKeys } from '../useGenerator';
import type { Equipment } from '../../types/equipment';
import { beginGeneratorPatch, settleGeneratorPatch } from './optimisticGeneratorPatches';
import toast from 'react-hot-toast';

export { toEquipmentPatch } from './optimisticGeneratorPatches';

/**
 * Fields accepted by the `createGenerator` / `updateGenerator` callables.
 *
 * Every key here MUST exist in the server's FIELD_MAP
 * (functions/src/projects/generatorApi.ts) or be an explicit create-path
 * parameter — anything else is silently dropped server-side while the client
 * still shows a success toast.
 *
 * Deliberately ABSENT (do not re-add without changing AppSheet field ownership
 * in functions/src/dataHub/appsheet/fieldMaps/generators.ts first):
 * `verified`, `serviceContractFile`.
 *
 * `branch` is accepted on create AND edit since 2026-09-23 (Dillan): SHARED,
 * validated server-side against the canonical branch list. `status` is EDIT-only
 * (see UpdateGeneratorInput) — a new unit always starts "Active".
 *
 * `amps`, `ats2` and `nextScheduledVisitReminder` are EDIT-path fields (see
 * UpdateGeneratorInput): Nick adjudicated them editable 2026-09-09 and they are
 * SHARED + mapped server-side. They are not create-path parameters — the Create
 * modal does not send them.
 */
export interface CreateGeneratorInput {
  clientName: string;
  /** AppSheet "Generator Type". NOT `equipmentType` — the server rejects that. */
  generatorType: string;
  /** One of `BRANCHES` (types/branch.ts); the server rejects anything else. */
  branch?: string;
  managementCompany?: string;
  siteName?: string;
  clientNameSubAccount?: string;
  clientType?: string;
  qboId?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  gps?: string;
  accessInstructions?: string;
  generatorKW?: string;
  voltage?: string;
  fuel?: string;
  generatorMake?: string;
  generatorModel?: string;
  generatorSerialNumber?: string;
  generatorSpecNumber?: string;
  generatorNotes?: string;
  engineMake?: string;
  engineModel?: string;
  engineSerialNumber?: string;
  atsMake?: string;
  atsModel?: string;
  atsSerialNumber?: string;
  /** The create form's "second ATS" toggle; the server stores it as "Y"/"N" and it wins over the details (#4594). */
  ats2?: boolean;
  ats2Make?: string;
  ats2Model?: string;
  ats2SerialNumber?: string;
  alternatorMake?: string;
  alternatorModel?: string;
  alternatorSerialNumber?: string;
  installDate?: string;
  opgInstalled?: boolean;
  serviceContract?: boolean;
  warrantyExpires?: string;
  prevailingWage?: boolean;
  inactiveNotes?: string;
  clientNotes?: string;
  /**
   * Per-form-session idempotency key (a UUID). The server locks on it BEFORE
   * creating the AppSheet row, so a double-click / retry-after-slow-response
   * cannot mint two generators. Callers MUST generate a NEW key for each
   * genuinely new unit (see CreateEquipmentModal) — reusing a key inside the
   * server's replay window returns the earlier generator instead of creating one.
   */
  idempotencyKey?: string;
}

export interface UpdateGeneratorInput {
  generatorId: string;
  updates: Partial<CreateGeneratorInput> & {
    /**
     * Editable since 2026-09-09 (Nick's gated-fields adjudication). Server-side:
     * `amps` must be a non-negative digit string ("" clears — FSR's genAmps field
     * validates integer), `ats2` must be a real boolean (coerced to "Y"/"N" in the
     * mirror so FSR pre-fill keeps working), the reminder is free text ≤500 chars.
     */
    amps?: string;
    ats2?: boolean;
    nextScheduledVisitReminder?: string;
    /**
     * Editable since 2026-09-23 (Dillan). One of `EDITABLE_GENERATOR_STATUSES` — the
     * server refuses Credit Hold / Inactive / Removed (as target or current value) and
     * the derived Active Scheduled, because those belong to workflows that also change
     * the unit's recurring schedules. Written to
     * AppSheet FIRST and only then mirrored, because an AppSheet automation also
     * writes Status — if AppSheet refuses it, the WHOLE save fails and nothing in
     * this request is saved. Send only when the user changed it.
     */
    status?: string;
    /**
     * Generator to QuickBooks-customer link — who gets billed for this unit.
     *
     * OfficeHub-owned and Firestore-only: the AppSheet `QBO_ID` column is dead
     * (`functions/src/billing/services/qboCustomerResolver.ts` says so and reads
     * `qboLinks` instead), so this never crosses into AppSheet and needs no field
     * ownership promotion. The billing spine treats it as its strongest customer
     * signal — but a history-derived one, so it corroborates against the ticket's
     * own customer name rather than auto-stamping blind.
     *
     * `docId` MUST be exactly `${realmId}_${customerRef}`; the callable validates
     * that shape and rejects anything else. Send the whole array — the server
     * replaces it wholesale rather than merging.
     */
    qboLinks?: Array<{
      realmId: string;
      customerRef: string;
      customerName: string;
      docId: string;
    }>;
  };
}

/**
 * Prefix of every placeholder id a create inserts before AppSheet has minted the
 * real Generator ID. Lists use `isPendingGeneratorId` to render these rows as
 * "Creating…" and to NOT link them: `/field-service/generators/pending-…` is a page
 * for a unit that does not exist yet.
 */
export const PENDING_GENERATOR_ID_PREFIX = 'pending-';

export function isPendingGeneratorId(id: string | undefined): boolean {
  return typeof id === 'string' && id.startsWith(PENDING_GENERATOR_ID_PREFIX);
}

let pendingSeq = 0;

/** A fresh placeholder id, unique within this tab. */
export function newPendingGeneratorId(): string {
  pendingSeq += 1;
  return `${PENDING_GENERATOR_ID_PREFIX}${Date.now()}-${pendingSeq}`;
}

/** Build a minimal optimistic Equipment record from create input. */
export function buildOptimisticEquipment(
  input: CreateGeneratorInput,
  id: string = newPendingGeneratorId()
): Equipment {
  const now = Timestamp.now();
  return {
    equipmentId: id,
    organizationId: '',
    customerName: input.clientName,
    managementCompany: input.managementCompany,
    clientNameSubAccount: input.clientNameSubAccount,
    siteName: input.siteName,
    address: {
      street: input.street ?? '',
      city: input.city ?? '',
      state: input.state ?? '',
      zip: input.zip ?? '',
    },
    gps: input.gps,
    accessInstructions: input.accessInstructions,
    equipmentType: input.generatorType as Equipment['equipmentType'],
    // AppSheet assigns Status on its side; new rows read back as Active.
    status: 'Active',
    generatorKW: input.generatorKW !== undefined ? Number(input.generatorKW) : undefined,
    voltage: input.voltage,
    fuel: input.fuel,
    generatorMake: input.generatorMake,
    generatorModel: input.generatorModel,
    generatorSerialNumber: input.generatorSerialNumber,
    generatorSpecNumber: input.generatorSpecNumber,
    generatorNotes: input.generatorNotes,
    engineMake: input.engineMake,
    engineModel: input.engineModel,
    engineSerialNumber: input.engineSerialNumber,
    atsMake: input.atsMake,
    atsModel: input.atsModel,
    atsSerialNumber: input.atsSerialNumber,
    ats2Make: input.ats2Make,
    ats2Model: input.ats2Model,
    ats2SerialNumber: input.ats2SerialNumber,
    alternatorMake: input.alternatorMake,
    alternatorModel: input.alternatorModel,
    alternatorSerialNumber: input.alternatorSerialNumber,
    installDate: input.installDate ? new Date(input.installDate) : undefined,
    opgInstalled: input.opgInstalled,
    serviceContract: input.serviceContract,
    warrantyExpires: input.warrantyExpires ? new Date(input.warrantyExpires) : undefined,
    clientNotes: input.clientNotes,
    clientType: input.clientType as Equipment['clientType'],
    inactiveNotes: input.inactiveNotes,
    prevailingWage: input.prevailingWage,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Create a new generator via Cloud Function.
 *
 * @example
 * const { mutate } = useCreateGenerator();
 * mutate({ clientName: 'Acme Corp', equipmentType: 'Liquid-Cooled Standby Gen' });
 */
export function useCreateGenerator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateGeneratorInput) => {
      const fn = httpsCallable<
        CreateGeneratorInput,
        { id: string; generatorId?: string; replayed?: boolean }
      >(functions, 'createGenerator');
      const result = await fn(input);
      return result.data;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: dataHubEquipmentKeys.all });

      const previous = queryClient.getQueryData<Equipment[]>(
        dataHubEquipmentKeys.list()
      );

      // Optimistically insert a temp record at the top of the list
      const optimistic = buildOptimisticEquipment(input);
      queryClient.setQueryData<Equipment[]>(
        dataHubEquipmentKeys.list(),
        (old = []) => [optimistic, ...old]
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(dataHubEquipmentKeys.list(), context.previous);
      } else {
        // The optimistic insert fabricated the list entry from scratch;
        // setQueryData(key, undefined) is a no-op, so drop it instead.
        queryClient.removeQueries({
          queryKey: dataHubEquipmentKeys.list(),
          exact: true,
        });
      }
      toast.error('Failed to create generator');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all });
      // The Field Service generators list reads the same collection.
      queryClient.invalidateQueries({ queryKey: ['generators'] });
    },
    onSuccess: () => {
      toast.success('Generator created');
    },
  });
}

export interface UseUpdateGeneratorOptions {
  /**
   * Serialise this hook's saves with every other save carrying the same unit id: a second save
   * of the unit waits for the first (its optimistic cache update still happens at click time).
   * Saves of other units are unaffected.
   */
  readonly scopeUnitId?: string;
  /**
   * The caller reports success / failure itself (see lib/backgroundSave), so the hook shows no
   * toast of its own. Cache rollback and revalidation are unchanged.
   */
  readonly quiet?: boolean;
}

/** What `updateGenerator` returns (only the part the screens read). */
export interface UpdateGeneratorResult {
  success?: boolean;
  appSheetSynced?: boolean;
  appSheetQueued?: boolean;
  /** Present when the save kept text a make / model / serial held in that component's notes: one plain sentence. */
  keptMessage?: string;
}

/**
 * Update an existing generator via Cloud Function.
 *
 * NAME COLLISION: there is a second, unrelated `useUpdateGenerator` export in
 * `hooks/useGenerator.ts` used by the Field Service pages. That one is curried
 * by id — `useUpdateGenerator(id).mutate(updates)` — and patches the
 * `generatorKeys.detail(id)` cache. This one takes `{ generatorId, updates }`
 * and patches the DataHub equipment caches. Both call the same
 * `updateGenerator` callable. Import explicitly and check which you mean.
 *
 * @example
 * const { mutate } = useUpdateGenerator();
 * mutate({ generatorId: 'abc123', updates: { generatorMake: 'Kohler' } });
 */
export function useUpdateGenerator(options: UseUpdateGeneratorOptions = {}) {
  const queryClient = useQueryClient();
  const { scopeUnitId, quiet = false } = options;

  return useMutation({
    scope: scopeUnitId ? { id: `unit-write-${scopeUnitId}` } : undefined,
    mutationFn: async ({ generatorId, updates }: UpdateGeneratorInput): Promise<UpdateGeneratorResult | undefined> => {
      // The server may run for minutes (live AppSheet status read + Edit + writeback);
      // the SDK's 70s default would report a still-running save as failed.
      const fn = httpsCallable<{ generatorId: string; updates: unknown }, UpdateGeneratorResult>(
        functions, 'updateGenerator', { timeout: UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS });
      const result = await fn({ generatorId, updates });
      return result?.data;
    },
    onMutate: async ({ generatorId, updates }) => {
      await queryClient.cancelQueries({ queryKey: dataHubEquipmentKeys.all });
      // The patch is recorded (not just applied) so overlapping saves of one unit roll back and
      // revalidate correctly — see optimisticGeneratorPatches.
      return { patchToken: beginGeneratorPatch(queryClient, generatorId, updates) };
    },
    onError: (err) => {
      // The rollback (settleGeneratorPatch, in onSettled) still happens unconditionally: we do not
      // know whether a deadline-exceeded save landed on the server, so the cache goes back to the
      // last KNOWN-good state rather than keeping an unconfirmed optimistic write. The
      // revalidation self-corrects a rolled-back-then-actually-succeeded save within moments.
      //
      // `EditEquipmentModal` shows its own "Still saving..." warning for deadline-exceeded; a
      // generic "Failed to update generator" here would contradict it.
      const code = (err as { code?: string } | null)?.code;
      if (quiet || code === 'functions/deadline-exceeded') return;
      toast.error('Failed to update generator');
    },
    // RETURNS the revalidation so the mutation is not "settled" (and the next scoped save does
    // not start) until the refetch has landed. Only the LAST pending save of the unit revalidates:
    // an earlier one refetching would flash old data over a later save's pending change.
    onSettled: (_data, err, { generatorId }, context) => {
      const lastPending = settleGeneratorPatch(queryClient, generatorId, context?.patchToken, !err);
      if (!lastPending) return undefined;
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all }),
        queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.detail(generatorId) }),
        // The Field Service module reads the SAME appsheet_generators docs under a
        // different cache namespace (generatorKeys / ['generators']). Without these,
        // saving from the Edit modal mounted on the FS generator page left that page
        // showing pre-edit values until the 5-minute staleTime expired.
        queryClient.invalidateQueries({ queryKey: generatorKeys.detail(generatorId) }),
        queryClient.invalidateQueries({ queryKey: ['generators'] }),
      ]);
    },
    onSuccess: () => {
      if (!quiet) toast.success('Generator updated');
    },
  });
}

/**
 * Delete a generator via Cloud Function.
 *
 * @example
 * const { mutate } = useDeleteGenerator();
 * mutate('abc123');
 */
/** What `deleteGenerator` returns: the optional counts appear only when non-zero. */
export interface DeleteGeneratorResult {
  success: boolean;
  archived: boolean;
  /** Recurring schedules of the archived unit that were NOT stood down: they may keep creating tickets. */
  schedulesNotStoodDown?: number;
  /** Stand-downs saved here whose push to AppSheet is queued. */
  schedulesQueued?: number;
}

/**
 * The warning to show after an archive, or null when every recurring schedule was stood down (or queued to be).
 * Archiving a unit must also stop its recurring schedules; one that was not stopped keeps generating tickets for a unit
 * that is out of service, so say so plainly instead of a bare "Generator deleted".
 */
export function archiveWarning(data: DeleteGeneratorResult | undefined): string | null {
  const n = data?.schedulesNotStoodDown ?? 0;
  return n > 0
    ? `Generator archived, but ${n} recurring schedule${n === 1 ? '' : 's'} could NOT be turned off and may keep creating tickets. Turn ${n === 1 ? 'it' : 'them'} off in Recurring Schedules.`
    : null;
}

export function useDeleteGenerator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (generatorId: string) => {
      const fn = httpsCallable<{ generatorId: string }, DeleteGeneratorResult>(functions, 'deleteGenerator');
      const result = await fn({ generatorId });
      return result.data;
    },
    onMutate: async (generatorId) => {
      await queryClient.cancelQueries({ queryKey: dataHubEquipmentKeys.all });

      const previous = queryClient.getQueryData<Equipment[]>(
        dataHubEquipmentKeys.list()
      );

      queryClient.setQueryData<Equipment[]>(
        dataHubEquipmentKeys.list(),
        (old = []) => old.filter((eq) => eq.equipmentId !== generatorId)
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(dataHubEquipmentKeys.list(), context.previous);
      }
      toast.error('Failed to delete generator');
    },
    onSettled: (_data, _err, generatorId) => {
      queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all });
      // Keep the Field Service caches (same underlying collection) in sync.
      queryClient.invalidateQueries({ queryKey: generatorKeys.detail(generatorId) });
      queryClient.invalidateQueries({ queryKey: ['generators'] });
    },
    onSuccess: (data) => {
      const warning = archiveWarning(data);
      if (warning) toast.error(warning, { duration: 10_000 });
      else toast.success('Generator deleted');
    },
  });
}

// ─── Status workflows (AS-GAPS PR 2) ──────────────────────────────────────────

/** The six AppSheet generator status actions done natively (projects_setGeneratorWorkflowStatus). */
export const GENERATOR_WORKFLOW_ACTIONS = ['applyCreditHold', 'removeCreditHold', 'markInactive', 'reactivate', 'markRemoved', 'markPastRental'] as const;
export type GeneratorWorkflowAction = (typeof GENERATOR_WORKFLOW_ACTIONS)[number];

export interface SetGeneratorWorkflowStatusInput {
  generatorId: string;
  action: GeneratorWorkflowAction;
  /** applyCreditHold / removeCreditHold (optional). */
  clientNotes?: string;
  /** markInactive (required); reactivate (optional). */
  inactiveNotes?: string;
  /** markRemoved (required): becomes Generator Notes. */
  removalNotes?: string;
  /** reactivate (required). */
  clientName?: string;
  /** reactivate (optional). */
  clientNameSubAccount?: string;
}

/**
 * The status each action sets, as the server's ACTIONS table has it (functions/src/projects/generatorStatusWorkflowService.ts).
 * Only used to show the change at click time; the server decides, and the revalidation replaces it with the truth.
 */
export const GENERATOR_WORKFLOW_TARGET_STATUS: Readonly<Record<GeneratorWorkflowAction, string>> = {
  applyCreditHold: 'Credit Hold',
  removeCreditHold: 'Active',
  markInactive: 'Inactive',
  reactivate: 'Active',
  markRemoved: 'Removed',
  markPastRental: 'Past Rental',
};

/** What the server reports (the part the screens read; the full shape is the server's SetGeneratorWorkflowStatusOutput). */
export interface SetGeneratorWorkflowStatusResult {
  success: true;
  action: GeneratorWorkflowAction;
  previousStatus: string;
  status: string;
  appSheetSynced: boolean;
  /** The status was already at the target in AppSheet; only the unfinished cascade ran (a retry). */
  retriedCascadeOnly: boolean;
  pausedScheduleIds: string[];
  resumedScheduleIds: string[];
  stillPausedScheduleIds: string[];
  /** Remembered ids that were no longer paused (archived or gone): forgotten, nothing to resume. */
  droppedScheduleIds: string[];
  resumedByAppSheet: string[];
  /** One plain sentence: what changed on which side. */
  summary: string;
}

/**
 * Run one generator status workflow (Credit Hold on/off with its schedule cascade, Mark Inactive, Re-Activate,
 * Remove, Past Rental). The SERVER decides the precondition on the LIVE AppSheet status and owns the cascade; a
 * refusal ends "Nothing was changed" and is shown verbatim. The new status shows at once (same patch/rollback as
 * {@link useUpdateGenerator}); a failure or a refusal puts the last KNOWN status back, and the caches are revalidated
 * when the server answers, because the status and the schedules move together there.
 */
export function useSetGeneratorWorkflowStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SetGeneratorWorkflowStatusInput): Promise<SetGeneratorWorkflowStatusResult> => {
      // A Credit Hold pauses the unit's schedules in AppSheet one by one before the status write: minutes, not
      // seconds. The SDK's 70 s default would report a hold that landed as failed.
      const fn = httpsCallable<SetGeneratorWorkflowStatusInput, SetGeneratorWorkflowStatusResult>(
        functions, 'projects_setGeneratorWorkflowStatus', { timeout: SET_GENERATOR_WORKFLOW_STATUS_CALLABLE_TIMEOUT_MS });
      const result = await fn(input);
      return result.data;
    },
    onMutate: async ({ generatorId, action }) => {
      await queryClient.cancelQueries({ queryKey: dataHubEquipmentKeys.all });
      return { patchToken: beginGeneratorPatch(queryClient, generatorId, { status: GENERATOR_WORKFLOW_TARGET_STATUS[action] }) };
    },
    onError: (err) => {
      const message = (err as { message?: string } | null)?.message?.trim();
      toast.error(message || 'Status change failed', { duration: 10_000 });
    },
    // The rollback runs on every failure (settleGeneratorPatch with succeeded=false): a refused or half-done workflow must not keep a status
    // the server did not confirm. RETURNS the revalidation so the mutation is not settled until the refetch has landed.
    onSettled: (_data, err, { generatorId }, context) => {
      const lastPending = settleGeneratorPatch(queryClient, generatorId, context?.patchToken, !err);
      if (!lastPending) return undefined;
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all }),
        queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.detail(generatorId) }),
        // The Field Service module reads the same appsheet_generators docs under its own cache namespace.
        queryClient.invalidateQueries({ queryKey: generatorKeys.detail(generatorId) }),
        queryClient.invalidateQueries({ queryKey: ['generators'] }),
      ]);
    },
    onSuccess: (data) => {
      toast.success(data?.summary || `Status set to ${data?.status ?? 'the new value'}`, { duration: 8_000 });
    },
  });
}
